import {
  EnrichedWorkspace,
  ServiceFilterOption,
  TagFilterOption,
  UsageBreakdownItem,
  UsageWithBreakdown,
  User,
  UserFilterOption,
  WorkspaceFilterOption,
} from '../types';

/**
 * Extract unique workspace UUIDs from workspace-grouped usage data
 * and convert them to workspace filter options
 *
 * @param usageData Usage data grouped by workspace
 * @param workspacesMap Map of workspace UUIDs to enriched workspace objects
 * @returns Array of workspace filter options sorted alphabetically
 */
export function extractWorkspaceFilterOptions(
  usageData: UsageWithBreakdown[] | undefined,
  workspacesMap: Map<string, EnrichedWorkspace>
): WorkspaceFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueWorkspaceUUIDs = new Set<string>();

  // Extract unique workspace UUIDs from usage data
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown: UsageBreakdownItem) => {
      if (breakdown.group) {
        uniqueWorkspaceUUIDs.add(breakdown.group);
      }
    });
  });

  // Convert UUIDs to filter options with display names
  const options: WorkspaceFilterOption[] = [];
  uniqueWorkspaceUUIDs.forEach((uuid) => {
    const workspace = workspacesMap.get(`workspaces/${uuid}`);
    if (workspace) {
      let displayName = workspace.displayName;
      // Handle Personal Workspace naming
      if (workspace.displayName === 'Personal Workspace') {
        displayName = `PW - ${workspace.userDisplayName}`;
      }

      options.push({
        key: uuid,
        text: displayName,
      });
    } else {
      // Workspace not found in map, use UUID as fallback
      console.warn('[extractWorkspaceFilterOptions] Workspace not found in map:', uuid);
      options.push({
        key: uuid,
        text: uuid,
      });
    }
  });

  // Sort alphabetically by display name
  options.sort((a, b) => a.text.localeCompare(b.text));

  return options;
}

/**
 * Extract unique user UUIDs from user-grouped usage data
 * and convert them to user filter options
 *
 * @param usageData Usage data grouped by user
 * @param usersMap Map of user UUIDs to user objects
 * @returns Array of user filter options sorted alphabetically
 */
export function extractUserFilterOptions(
  usageData: UsageWithBreakdown[] | undefined,
  usersMap: Map<string, User>
): UserFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueUserUUIDs = new Set<string>();

  // Extract unique user UUIDs from usage data
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown: UsageBreakdownItem) => {
      if (breakdown.group) {
        uniqueUserUUIDs.add(breakdown.group);
      }
    });
  });

  // Convert UUIDs to filter options with display names
  const options: UserFilterOption[] = [];
  uniqueUserUUIDs.forEach((uuid) => {
    const user = usersMap.get(`users/${uuid}`);
    if (user) {
      const displayName = user.displayName || user.loginPrincipal || uuid;
      options.push({
        key: uuid,
        text: displayName,
      });
    } else {
      // User not found in map, use UUID as fallback
      console.warn('[extractUserFilterOptions] User not found in map:', uuid);
      options.push({
        key: uuid,
        text: uuid,
      });
    }
  });

  // Sort alphabetically by display name
  options.sort((a, b) => a.text.localeCompare(b.text));
  return options;
}

/**
 * Extract unique tag values from tag-grouped usage data
 * and convert them to tag filter options
 *
 * @param usageData Usage data grouped by tag
 * @returns Array of tag filter options sorted alphabetically
 */
export function extractTagFilterOptions(usageData: UsageWithBreakdown[] | undefined): TagFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueTags = new Set<string>();

  // Extract unique tags from usage data
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown: UsageBreakdownItem) => {
      if (breakdown.group) {
        uniqueTags.add(breakdown.group);
      }
    });
  });

  // Convert tags to filter options (key and text are the same for tags)
  const options: TagFilterOption[] = [];
  uniqueTags.forEach((tag) => {
    options.push({
      key: tag,
      text: tag,
    });
  });

  // Sort alphabetically
  options.sort((a, b) => a.text.localeCompare(b.text));
  return options;
}

/**
 * Extract unique service values from service-grouped usage data
 * and convert them to service filter options
 *
 * @param usageData Usage data grouped by service
 * @returns Array of service filter options sorted alphabetically
 */
export function extractServiceFilterOptions(usageData: UsageWithBreakdown[] | undefined): ServiceFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueServices = new Set<string>();

  // Extract unique services from usage data
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown: UsageBreakdownItem) => {
      if (breakdown.group) {
        uniqueServices.add(breakdown.group);
      }
    });
  });

  // Convert services to filter options (key and text are the same for services)
  const options: ServiceFilterOption[] = [];
  uniqueServices.forEach((service) => {
    options.push({
      key: service,
      text: service,
    });
  });

  // Sort alphabetically
  options.sort((a, b) => a.text.localeCompare(b.text));
  return options;
}
